/**
 * The builder's day dock: `features/plan-day-programming`'s `useDayDock`, told where this page keeps
 * the open day.
 *
 * The address owns which day is open and in which box (`usePlanBuilderSearch`), and which box a day
 * OPENS in is the remembered default (`model/dockDefault`). Both were inside the hook until it moved
 * down to the feature on 2 October 2026 for its second host; they are this page's, so they stay.
 */

import { useDayDock, type DayDockState } from '~/features/plan-day-programming';
import { preferenceStore } from '~/shared/lib/preferenceStore';

import { readDockDefault, writeDockDefault } from '../model/dockDefault';

import { usePlanBuilderSearch } from './usePlanBuilderSearch';

export function useBuilderDayDock(): DayDockState {
  const search = usePlanBuilderSearch();
  return useDayDock({
    daySlotToken: search.daySlotToken,
    dock: search.dock,
    // Read at the moment of the press rather than held in state: a second tab that maximised a day
    // writes the same key, and reading it late is what makes this tab agree on the next press.
    defaultDock: () => readDockDefault(preferenceStore()),
    open: (daySlotToken, dock) => {
      search.update({ day: daySlotToken, dock }, { push: true });
    },
    select: (daySlotToken) => {
      search.update({ day: daySlotToken });
    },
    close: () => {
      search.update({ day: undefined, dock: undefined });
    },
    switchHost: (next) => {
      // The default is written BEFORE the address moves, so the preference is kept even if the
      // navigation is the last thing this tab does.
      writeDockDefault(preferenceStore(), next);
      search.update({ dock: next });
    },
  });
}
